import { useSyncExternalStore } from "react";

export type Answers = Record<string, string[]>;
export type AnswerIndices = Record<string, number[]>; // questionId → optionIndex[] for backend
export type ConditionRow = { question: string; answer: string };

export type PickupDetails = { date?: string; timeSlot?: string; name?: string; address?: string; landmark?: string; city?: string; pincode?: string; };

export type SellState = {
  brandId?: string; modelId?: string;
  productId?: string;    // real _id from backend
  product?: any;         // full backend product object
  algoId?: string;       // real _id from backend
  algoBasePrice?: number;
  phone?: string; verified: boolean; customerId?: string;
  answers: Answers;
  answerIndices: AnswerIndices;
  conditionSummary: ConditionRow[];
  finalPrice?: number;
  pickup?: PickupDetails;
  referenceNo?: string; orderId?: string;
};

const STORAGE_KEY = "sellMyGadget_sell_state";

function load(): SellState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { verified: false, answers: {}, answerIndices: {}, conditionSummary: [], ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { verified: false, answers: {}, answerIndices: {}, conditionSummary: [] };
}

let state: SellState = load();
const listeners = new Set<() => void>();
function notify() { listeners.forEach(l => l()); }
function set(patch: Partial<SellState>) {
  state = { ...state, ...patch };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  notify();
}

export const sellStore = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  setDevice: (brandId: string, modelId: string, productId?: string, algoId?: string, algoBasePrice?: number, product?: any) =>
    set({ brandId, modelId, productId, algoId, algoBasePrice, product }),
  setPhone: (phone: string) => set({ phone }),
  setVerified: (verified: boolean, customerId?: string) => set({ verified, customerId }),
  setAnswer: (k: string, v: string, questionId?: string, optionIndex?: number) => {
    const current = state.answers[k] || [];
    const exists = current.includes(v);
    const answers = {
      ...state.answers,
      [k]: exists ? current.filter(val => val !== v) : [...current, v]
    };
    const answerIndices = { ...state.answerIndices };
    if (questionId !== undefined && optionIndex !== undefined) {
      const currentIndices = answerIndices[questionId] || [];
      const indexExists = currentIndices.includes(optionIndex);
      answerIndices[questionId] = indexExists
        ? currentIndices.filter(i => i !== optionIndex)
        : [...currentIndices, optionIndex];
    }
    set({ answers, answerIndices });
  },
  setConditionSummary: (conditionSummary: ConditionRow[]) => set({ conditionSummary }),
  setPrice: (finalPrice: number) => set({ finalPrice }),
  setPickup: (pickup: PickupDetails) => set({ pickup }),
  setReference: (referenceNo: string, orderId?: string) => set({ referenceNo, orderId }),
  reset: () => { try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ } set({ brandId: undefined, modelId: undefined, productId: undefined, product: undefined, algoId: undefined, algoBasePrice: undefined, phone: undefined, verified: false, customerId: undefined, answers: {}, answerIndices: {}, conditionSummary: [], finalPrice: undefined, pickup: undefined, referenceNo: undefined, orderId: undefined }); },
};

export function useSell(): SellState {
  return useSyncExternalStore(sellStore.subscribe, sellStore.get, sellStore.get);
}
